'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface ModerationComment {
  id: string;
  video_id: string;
  author: string;
  body: string;
  commenter_email?: string;
  created_at: string;
  moderation_status: 'pending' | 'approved' | 'flagged' | 'rejected';
  spam_score: number;
  flagged_reason?: string;
  moderated_at?: string;
  is_deleted: boolean;
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'flagged' | 'rejected';

const SPAM_KEYWORDS = [
  'click here', 'buy now', 'free money', 'earn cash', 'make money fast',
  'visit my profile', 'check my link', 'follow me', 'subscribe to me',
  'http://', 'www.', '.com/ref', 'promo code', 'discount code',
  'casino', 'betting', 'crypto', 'bitcoin', 'nft', 'onlyfans',
];

function detectSpam(body: string): { score: number; reasons: string[] } {
  const lower = body.toLowerCase();
  const reasons: string[] = [];
  let score = 0;

  SPAM_KEYWORDS.forEach(kw => {
    if (lower.includes(kw)) {
      score += 20;
      reasons.push(`Contains "${kw}"`);
    }
  });

  // All caps check
  const words = body.split(' ').filter(w => w.length > 3);
  const capsWords = words.filter(w => w === w.toUpperCase() && /[A-Z]/.test(w));
  if (words.length > 0 && capsWords.length / words.length > 0.5) {
    score += 15;
    reasons.push('Excessive caps');
  }

  // Repeated characters
  if (/(.)\1{4,}/.test(body)) {
    score += 10;
    reasons.push('Repeated characters');
  }

  // Very short comment
  if (body.trim().length < 5) {
    score += 10;
    reasons.push('Very short');
  }

  // Multiple exclamation marks
  if ((body.match(/!/g) || []).length > 3) {
    score += 10;
    reasons.push('Excessive exclamation marks');
  }

  return { score: Math.min(score, 100), reasons };
}

export default function AdminCommentModeration() {
  const supabase = createClient();

  const [comments, setComments] = useState<ModerationComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [flagReasonInput, setFlagReasonInput] = useState<{ id: string; reason: string } | null>(null);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('comments')
        .select('id, video_id, author, body, commenter_email, created_at, moderation_status, spam_score, flagged_reason, moderated_at, is_deleted')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(200);

      if (filterStatus !== 'all') {
        query = query.eq('moderation_status', filterStatus);
      }

      const { data, error: err } = await query;
      if (err) throw err;
      setComments(data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, supabase]);

  useEffect(() => {
    fetchComments();
    setSelectedIds(new Set());
  }, [fetchComments]);

  const updateStatus = async (id: string, status: ModerationComment['moderation_status'], flaggedReason?: string) => {
    setActionLoading(id);
    try {
      const update: Record<string, unknown> = {
        moderation_status: status,
        moderated_at: new Date().toISOString(),
      };
      if (flaggedReason !== undefined) update.flagged_reason = flaggedReason;

      const { error: err } = await supabase.from('comments').update(update).eq('id', id);
      if (err) throw err;
      setComments(prev => prev.map(c => c.id === id ? { ...c, moderation_status: status, flagged_reason: flaggedReason ?? c.flagged_reason } : c));
      showSuccess(`Comment ${status}`);
    } catch (e: any) {
      setError(e.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async (status: ModerationComment['moderation_status']) => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      const { error: err } = await supabase
        .from('comments')
        .update({ moderation_status: status, moderated_at: new Date().toISOString() })
        .in('id', ids);
      if (err) throw err;
      setComments(prev => prev.map(c => selectedIds.has(c.id) ? { ...c, moderation_status: status } : c));
      setSelectedIds(new Set());
      showSuccess(`${ids.length} comment(s) ${status}`);
    } catch (e: any) {
      setError(e.message || 'Bulk action failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Permanently delete this comment?')) return;
    setActionLoading(id);
    try {
      const { error: err } = await supabase.from('comments').update({ is_deleted: true, moderation_status: 'rejected' }).eq('id', id);
      if (err) throw err;
      setComments(prev => prev.filter(c => c.id !== id));
      showSuccess('Comment deleted');
    } catch (e: any) {
      setError(e.message || 'Delete failed');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(c => c.id)));
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filtered = comments.filter(c => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.author.toLowerCase().includes(q) ||
      c.body.toLowerCase().includes(q) ||
      (c.commenter_email || '').toLowerCase().includes(q) ||
      c.video_id.toLowerCase().includes(q)
    );
  });

  const counts = {
    all: comments.length,
    pending: comments.filter(c => c.moderation_status === 'pending').length,
    approved: comments.filter(c => c.moderation_status === 'approved').length,
    flagged: comments.filter(c => c.moderation_status === 'flagged').length,
    rejected: comments.filter(c => c.moderation_status === 'rejected').length,
  };

  const statusBadge = (status: ModerationComment['moderation_status']) => {
    const map: Record<string, string> = {
      pending: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border border-green-500/30',
      flagged: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
      rejected: 'bg-red-500/20 text-red-400 border border-red-500/30',
    };
    return map[status] || '';
  };

  const spamBadgeColor = (score: number) => {
    if (score >= 60) return 'bg-red-500/20 text-red-400';
    if (score >= 30) return 'bg-orange-500/20 text-orange-400';
    return 'bg-surface-tertiary text-text-muted';
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">💬 Comment Moderation</h3>
          <p className="text-text-muted text-sm mt-0.5">Review, approve, or flag comments before they appear on videos</p>
        </div>
        <button
          onClick={fetchComments}
          className="text-xs text-text-muted hover:text-text-primary border border-stroke-primary rounded px-3 py-1.5 transition-colors"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Success / Error */}
      {successMsg && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 text-sm px-4 py-2.5 rounded-[4px]">
          ✓ {successMsg}
        </div>
      )}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-2.5 rounded-[4px]">
          {error}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-1 border-b border-stroke-primary overflow-x-auto pb-0">
        {(['pending', 'approved', 'flagged', 'rejected', 'all'] as FilterStatus[]).map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors capitalize ${
              filterStatus === s
                ? 'border-brand-red text-brand-red' :'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${filterStatus === s ? 'bg-brand-red/20 text-brand-red' : 'bg-surface-tertiary text-text-muted'}`}>
              {counts[s]}
            </span>
          </button>
        ))}
      </div>

      {/* Search + Bulk actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by author, email, content, video ID..."
          className="flex-1 min-w-[200px] bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-red"
        />
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-text-muted text-xs">{selectedIds.size} selected</span>
            <button
              onClick={() => handleBulkAction('approved')}
              disabled={bulkLoading}
              className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50"
            >
              ✓ Approve All
            </button>
            <button
              onClick={() => handleBulkAction('flagged')}
              disabled={bulkLoading}
              className="text-xs bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50"
            >
              ⚑ Flag All
            </button>
            <button
              onClick={() => handleBulkAction('rejected')}
              disabled={bulkLoading}
              className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50"
            >
              ✕ Reject All
            </button>
          </div>
        )}
      </div>

      {/* Flag reason modal */}
      {flagReasonInput && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-secondary border border-stroke-primary rounded-lg p-5 w-full max-w-sm space-y-4">
            <h4 className="text-text-primary font-semibold">Flag Comment</h4>
            <textarea
              value={flagReasonInput.reason}
              onChange={e => setFlagReasonInput(prev => prev ? { ...prev, reason: e.target.value } : null)}
              placeholder="Reason for flagging (optional)..."
              rows={3}
              className="w-full bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-red resize-none"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setFlagReasonInput(null)}
                className="text-sm text-text-muted hover:text-text-primary px-3 py-1.5 border border-stroke-primary rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateStatus(flagReasonInput.id, 'flagged', flagReasonInput.reason);
                  setFlagReasonInput(null);
                }}
                className="text-sm bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded transition-colors"
              >
                Flag Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments list */}
      {loading ? (
        <div className="flex items-center gap-2 text-text-muted py-8 justify-center">
          <div className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Loading comments...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <div className="text-4xl mb-3">💬</div>
          <p className="text-sm">No comments found{filterStatus !== 'all' ? ` with status "${filterStatus}"` : ''}.</p>
        </div>
      ) : (
        <div className="space-y-0 border border-stroke-primary rounded-lg overflow-hidden">
          {/* Select all row */}
          <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-tertiary border-b border-stroke-primary">
            <input
              type="checkbox"
              checked={selectedIds.size === filtered.length && filtered.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 accent-brand-red cursor-pointer"
            />
            <span className="text-text-muted text-xs">{filtered.length} comment{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {filtered.map((comment, idx) => {
            const spam = detectSpam(comment.body);
            const effectiveSpamScore = Math.max(comment.spam_score, spam.score);
            const isHighSpam = effectiveSpamScore >= 50;

            return (
              <div
                key={comment.id}
                className={`flex gap-3 px-4 py-4 ${idx < filtered.length - 1 ? 'border-b border-stroke-primary' : ''} ${isHighSpam ? 'bg-red-500/5' : ''} hover:bg-surface-tertiary/50 transition-colors`}
              >
                {/* Checkbox */}
                <div className="flex-shrink-0 pt-0.5">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(comment.id)}
                    onChange={() => toggleSelect(comment.id)}
                    className="w-4 h-4 accent-brand-red cursor-pointer"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-2">
                  {/* Meta row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-text-primary text-sm font-medium">{comment.author}</span>
                    {comment.commenter_email && (
                      <span className="text-text-muted text-xs">{comment.commenter_email}</span>
                    )}
                    <span className="text-text-muted text-xs">·</span>
                    <span className="text-text-muted text-xs">{formatDate(comment.created_at)}</span>
                    <span className="text-text-muted text-xs">·</span>
                    <span className="text-text-muted text-xs">Video: <span className="font-mono text-xs">{comment.video_id.slice(0, 8)}…</span></span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(comment.moderation_status)}`}>
                      {comment.moderation_status}
                    </span>
                    {effectiveSpamScore > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${spamBadgeColor(effectiveSpamScore)}`}>
                        Spam: {effectiveSpamScore}%
                      </span>
                    )}
                    {isHighSpam && (
                      <span className="text-xs text-red-400 font-medium">⚠ High spam risk</span>
                    )}
                  </div>

                  {/* Comment body */}
                  <p className="text-text-secondary text-sm leading-relaxed break-words">{comment.body}</p>

                  {/* Spam reasons */}
                  {spam.reasons.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {spam.reasons.map((r, i) => (
                        <span key={i} className="text-xs bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded">
                          {r}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Flagged reason */}
                  {comment.flagged_reason && (
                    <p className="text-xs text-orange-400 bg-orange-500/10 px-2 py-1 rounded">
                      ⚑ Flag reason: {comment.flagged_reason}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    {comment.moderation_status !== 'approved' && (
                      <button
                        onClick={() => updateStatus(comment.id, 'approved')}
                        disabled={actionLoading === comment.id}
                        className="text-xs bg-green-600/20 hover:bg-green-600/40 text-green-400 border border-green-600/30 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
                      >
                        ✓ Approve
                      </button>
                    )}
                    {comment.moderation_status !== 'flagged' && (
                      <button
                        onClick={() => setFlagReasonInput({ id: comment.id, reason: '' })}
                        disabled={actionLoading === comment.id}
                        className="text-xs bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 border border-orange-600/30 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
                      >
                        ⚑ Flag
                      </button>
                    )}
                    {comment.moderation_status !== 'rejected' && (
                      <button
                        onClick={() => updateStatus(comment.id, 'rejected')}
                        disabled={actionLoading === comment.id}
                        className="text-xs bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/30 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
                      >
                        ✕ Reject
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(comment.id)}
                      disabled={actionLoading === comment.id}
                      className="text-xs text-text-muted hover:text-red-400 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
                    >
                      🗑 Delete
                    </button>
                    {actionLoading === comment.id && (
                      <div className="w-3.5 h-3.5 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
